/**
 * Shared progress accumulator used by both the live WebSocket handler
 * (Workspace.jsx onMessage) and the history replay path
 * (parseTaskHistory.js). The two used to carry near-identical copies of
 * this logic — any silent divergence between them showed up as "live
 * view disagrees with refresh view", which is the exact bug we keep
 * hitting. Keeping a single source of truth prevents that.
 *
 * Behaviour of `accumulateProgressSteps(messages, steps, ts)`:
 *  - Filters out pending steps (description === "pending").
 *  - Scopes all upserts to cards AFTER the last user message — a new
 *    run (user types again) must not stitch its progress into an
 *    earlier run's badge card.
 *  - Pre-pass: closes any currently-running progress cards (and their
 *    running substeps) to "done" before applying the update. This is
 *    the defensive close-out for steps the agent stopped reporting —
 *    e.g. when the next phase starts without an explicit "Done" for
 *    the previous one.
 *  - For each active step, upserts by badge:
 *      - new badge  → push a fresh card (no substeps yet).
 *      - same badge, new title → append as a new substep, closing any
 *        still-running predecessor substep to "done".
 *      - same badge, same title → replace the last substep in place
 *        (the event is the same step evolving, not a new one).
 *  - Card-level status is "running" whenever any substep is running;
 *    otherwise it mirrors the latest substep's status. The card's
 *    top-level title/message always reflect the latest substep.
 *
 * Timestamp plumbing (2026-04-22, step-timings feature)
 * ─────────────────────────────────────────────────────
 * When a caller passes `ts` (ms since epoch — the event's wire
 * timestamp), the accumulator stamps:
 *   • card.started_at    — set when the card FIRST appears in any
 *     state that implies motion (running or already-done). Preserved
 *     across later events so the first-seen instant wins.
 *   • card.completed_at  — set the instant we observe the card
 *     transition out of "running" (same-badge running→done, or a
 *     different-badge arrival that closes it, or closeStaleRunningCards).
 *     Once set, never overwritten.
 *   • substep.running_at / substep.done_at  — same semantics at
 *     substep granularity, but ONLY once the card has an actual
 *     substeps array (i.e. after a second distinct substep arrives —
 *     we preserve the pre-existing invariant that a single-step card
 *     has NO `substeps` key at all). On promotion we seed the first
 *     substep with the card's started_at/completed_at so the per-row
 *     duration pills reflect the same timing the card-level row shows.
 *
 * Omitting `ts` (legacy callers, pre-timestamp test fixtures) leaves
 * these fields undefined — downstream UI renders a "—" placeholder and
 * the summary bar gracefully shows "not available" rather than a wrong
 * number. All the existing test cases keep passing because the call
 * signature is backward compatible AND the single-step card shape
 * (no substeps key) is preserved.
 */

const isStepPending = (step) => {
  const status = (step?.status ?? "").trim().toLowerCase();
  const detail = (step?.detail ?? "").trim().toLowerCase();
  // A replay/program announces future nodes as Skipped + Queued. They
  // have not started: stamping a terminal time here would freeze their
  // eventual duration at zero in both the live feed and saved history.
  return status === "pending" || status === "queued" ||
    (status === "skipped" && detail === "queued") ||
    (step?.description ?? "").trim().toLowerCase() === "pending";
};

function findLastUserIdx(messages) {
  // User messages mark run boundaries — progress events after a user
  // message must NOT stitch into cards from the previous run. BUT
  // HITL answers (tagged with `hitl: true` by the onContinue / onSend
  // handlers) are in-run continuations, not new runs. If we treated
  // them as run boundaries the agent's post-resume progress replay
  // would push brand-new duplicate cards for every phase that already
  // ran before the interrupt — which is exactly what user reported
  // 2026-04-23 ("I confirmed the HITL option and Identify re-appeared
  // as a fresh card below the user chip").
  let idx = -1;
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (m?.role !== "user") continue;
    if (m.hitl) continue;
    idx = i;
  }
  return idx;
}

/**
 * Close a substep that was running — preserving existing timestamps.
 * If the substep was already "done" we leave it alone. Idempotent.
 */
function closeSubstep(s, ts) {
  if (s.status !== "running") return s;
  const next = { ...s, status: "done" };
  if (ts != null && next.done_at == null) next.done_at = ts;
  return next;
}

function closeStaleRunningCards(messages, searchStart, activeBadges, ts) {
  // Close any running card whose badge is NOT mentioned in the current
  // event. Intent: when the agent moves from one phase to another
  // without emitting explicit "Done" for the old phase, we presume the
  // old phase is done. Same-badge running→done transitions are handled
  // inside upsertStep, so we skip those here — otherwise re-asserting
  // an older substep of an active badge would prematurely close the
  // still-running substep of the same badge.
  const out = messages.slice();
  for (let idx = searchStart; idx < out.length; idx++) {
    const m = out[idx];
    if (m?.role !== "aria" || m.progressCard?.status !== "running") continue;
    if (activeBadges.has(m.progressCard.badge)) continue;
    const prevSubsteps = m.progressCard.substeps;
    const closedSubsteps = prevSubsteps
      ? prevSubsteps.map((s) => closeSubstep(s, ts))
      : undefined;
    const cardPatch = {
      ...m.progressCard,
      status: "done",
      ...(closedSubsteps ? { substeps: closedSubsteps } : {}),
    };
    if (cardPatch.completed_at == null) {
      // Prefer the latest substep's done_at (if substeps exist) so the
      // card-level timing matches what the user sees on the last row;
      // otherwise fall back to the event ts.
      const lastDoneAt = closedSubsteps?.length
        ? closedSubsteps[closedSubsteps.length - 1].done_at
        : undefined;
      if (lastDoneAt != null) cardPatch.completed_at = lastDoneAt;
      else if (ts != null) cardPatch.completed_at = ts;
    }
    out[idx] = { ...m, progressCard: cardPatch };
  }
  return out;
}

function upsertStep(messages, searchStart, step, ts) {
  const badge = step.label;
  const newStatus = (step.status ?? "").toLowerCase();
  const newTitle = step.title;
  const newMessage = step.description;

  let existingIdx = -1;
  for (let idx = searchStart; idx < messages.length; idx++) {
    if (
      messages[idx]?.role === "aria" &&
      messages[idx].progressCard?.badge === badge
    ) {
      existingIdx = idx;
      break;
    }
  }

  const out = messages.slice();

  if (existingIdx === -1) {
    // Fresh card. Preserve the pre-existing shape — NO `substeps` key
    // on a single-step card (see progressAccumulator.test.js line 73).
    // Timestamps live on the card itself in this state.
    const cardPatch = {
      type: "progress_card",
      badge,
      title: newTitle,
      message: newMessage,
      details: null,
      status: newStatus,
    };
    if (ts != null) {
      cardPatch.started_at = ts;
      if (newStatus !== "running") {
        // Already done on first arrival — duration = 0, both endpoints
        // pinned to the same instant.
        cardPatch.completed_at = ts;
      }
    }
    out.push({ role: "aria", progressCard: cardPatch });
    return out;
  }

  const existing = out[existingIdx].progressCard;
  // Seed substeps from the card's current title/message if we haven't
  // started a substep list yet, so the prior entry isn't lost when the
  // second sub-step arrives. Seed timestamps from the card-level
  // started_at / completed_at we captured on first-arrival so the
  // per-row pills match the single-step card's pill before promotion.
  const prevSubsteps =
    existing.substeps ??
    [
      {
        title: existing.title,
        message: existing.message,
        status: existing.status,
        ...(existing.started_at != null
          ? { running_at: existing.started_at }
          : {}),
        ...(existing.completed_at != null
          ? { done_at: existing.completed_at }
          : {}),
      },
    ];
  const lastSub = prevSubsteps[prevSubsteps.length - 1];

  let nextSubsteps;
  if (lastSub && lastSub.title === newTitle) {
    // Same substep evolving — preserve whatever timestamps we stamped
    // earlier; only stamp new fields on actual status transitions.
    const merged = {
      ...lastSub,
      title: newTitle,
      message: newMessage,
      status: newStatus,
    };
    if (ts != null) {
      if (newStatus === "running" && merged.running_at == null) {
        merged.running_at = ts;
      }
      if (
        lastSub.status === "running" &&
        newStatus !== "running" &&
        merged.done_at == null
      ) {
        merged.done_at = ts;
      }
      // Defensive stamping (harmoniq-0-5-66 bug #5a). The original
      // logic only stamped done_at on a literal running→non-running
      // transition. That misses edge cases like pending→done,
      // done→done (duplicate event), or any path where the
      // pseudo-substep synthesized from a timing-less card arrives
      // here without running_at. Without a fallback, these substeps
      // end up displayed forever without a duration pill.
      //
      // Make both endpoints presence-guaranteed when the substep is
      // in a terminal state:
      //   • running_at missing + terminal status → stamp to ts
      //     (this is the current event; not the historical start,
      //     but honest: "we know it's done; duration >= 0")
      //   • done_at missing + terminal status → stamp to ts
      const terminal =
        newStatus !== "running" && newStatus !== "pending" && newStatus !== "";
      if (terminal) {
        if (merged.running_at == null) merged.running_at = ts;
        if (merged.done_at == null) merged.done_at = ts;
      }
    }
    nextSubsteps = [...prevSubsteps.slice(0, -1), merged];
  } else {
    // A different-title event arrives — close any still-running
    // predecessor (stamping done_at) before appending the new one.
    const closedPrev = prevSubsteps.map((s) => closeSubstep(s, ts));
    const newSub = {
      title: newTitle,
      message: newMessage,
      status: newStatus,
    };
    if (ts != null) {
      if (newStatus === "running") {
        newSub.running_at = ts;
      } else {
        // Direct-to-terminal substep (done / failed / skipped /
        // cancelled) — the agent emitted just one event for this
        // substep, never a preceding "Running" pulse. Pre-fix we
        // only stamped ``done_at`` here, so the substep had one
        // endpoint and the DurationPill (which needs both) returned
        // null — no pill rendered at all. Observed on the ANALYZE
        // phase of a template-run (harmoniq-0-5-65 bug #8).
        //
        // Fix: stamp BOTH endpoints to the same ts. The duration
        // renders as "0s" — honest about our lack of precise
        // per-step timing, but the pill is visible so the user
        // isn't left wondering whether the step was timed at all.
        newSub.running_at = ts;
        newSub.done_at = ts;
      }
    }
    nextSubsteps = [...closedPrev, newSub];
  }

  const anyRunning = nextSubsteps.some((s) => s.status === "running");
  const cardPatch = {
    type: "progress_card",
    badge,
    title: newTitle,
    message: newMessage,
    details: null,
    status: anyRunning ? "running" : newStatus,
    substeps: nextSubsteps,
  };

  // Propagate timing fields — first-run stamps card.started_at from
  // the earliest substep; card.completed_at is set when the card
  // exits "running". Never overwrite a previously-captured started_at.
  if (existing.started_at != null) {
    cardPatch.started_at = existing.started_at;
  } else if (ts != null) {
    const firstTs =
      nextSubsteps.find((s) => s.running_at != null)?.running_at ??
      nextSubsteps[0]?.done_at ??
      ts;
    cardPatch.started_at = firstTs;
  }
  // ── completed_at (harmoniq-0-5-80 bugfix) ────────────────────
  //
  // Pre-fix the preservation branch ran regardless of the card's
  // current running state:
  //
  //   if (existing.completed_at != null) {
  //     cardPatch.completed_at = existing.completed_at;   // always preserved
  //   } else if (!anyRunning && ts != null) { ... }
  //
  // That broke multi-substep phases the user flagged (EXTRACT
  // 10m + 37m showing as 10m on the bar pill). Two failure modes:
  //
  //   A) Running → Done → new-Running → Done
  //      Substep A transitioning to Done stamps the card's
  //      completed_at at A.done_at (~10m). When substep B arrives
  //      Running the stale stamp was preserved; when B finished
  //      the stamp was preserved AGAIN — so card.completed_at
  //      never advanced to B's done_at (~47m).
  //
  //   B) Done → Done (direct-to-done sequential substeps)
  //      Substep A's single Done event stamps the card. Substep B
  //      arriving Done goes through the different-title branch,
  //      pushes itself onto substeps, but preserve-existing locked
  //      completed_at at A's time.
  //
  // Fix: recompute ``completed_at`` from the current substeps on
  // every terminal state. Since substep.done_at values are
  // already preserved through merges (the accumulator never
  // overwrites an existing done_at), recomputing from the latest
  // substep's done_at is idempotent for duplicate events AND
  // correctly advances across sequential substeps. The old
  // "preserve existing" branch was solving an idempotency problem
  // that the substep-level timestamps already handle more robustly.
  //
  // When ``anyRunning`` is true, clear the field — a running card
  // isn't "done" by definition, and leaving a stale stamp confuses
  // useTaskTimings' duration math.
  //
  // ``ts == null`` path preserves the legacy no-timestamp fallback
  // for callers that don't provide a wire timestamp (old tests /
  // backward-compat): keep whatever was there.
  if (anyRunning) {
    // Card is running again — any prior completed_at is stale.
    // Deliberately do NOT set cardPatch.completed_at; let it go
    // back to undefined. The next terminal transition will stamp.
  } else if (ts != null) {
    // Card is terminal — recompute from the LATEST substep's
    // done_at so sequential substeps correctly accumulate their
    // real end time on the card. Falls back to ts when no substep
    // has a done_at yet (shouldn't happen in practice since
    // anyRunning=false means every substep is terminal, but
    // defensive).
    const lastDone = [...nextSubsteps]
      .reverse()
      .find((s) => s.done_at != null);
    cardPatch.completed_at = lastDone ? lastDone.done_at : ts;
  } else if (existing.completed_at != null) {
    // Legacy no-timestamp path — preserve whatever the card had
    // before. Tests pre-dating the timestamp plumbing rely on
    // this shape.
    cardPatch.completed_at = existing.completed_at;
  }

  out[existingIdx] = {
    role: "aria",
    progressCard: cardPatch,
  };
  return out;
}

/**
 * @param {Array}        messages — current messages array.
 * @param {Array}        steps    — steps[] from the progress event.
 * @param {number|null}  [ts]     — event timestamp in ms since epoch.
 *   Optional; when omitted no timing fields are stamped (legacy/test
 *   callers keep working).
 * @returns {Array} — new messages array.
 */
export function accumulateProgressSteps(messages, steps, ts) {
  const activeSteps = (Array.isArray(steps) ? steps : []).filter(
    (s) => !isStepPending(s),
  );
  if (activeSteps.length === 0) return messages;

  const searchStart = findLastUserIdx(messages) + 1;
  const activeBadges = new Set(activeSteps.map((s) => s.label));
  let out = closeStaleRunningCards(messages, searchStart, activeBadges, ts);
  for (const step of activeSteps) {
    out = upsertStep(out, searchStart, step, ts);
  }
  return out;
}
